import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DisplayDay, Payment, StatusConfig, YearDay, YearMonth } from 'src/app/shared/models/calendar.models';

@Component({
  selector: 'fun-calendar-pay-timetable',
  templateUrl: './calendar-pay-timetable.component.html',
  styles: [],
})
export class CalendarPayTimetableComponent implements OnInit {

  @Input() calendarBenefits: any;
  @ViewChild('detailContainer') detailContainer!: ElementRef;

  monthNames: string[] = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
  ];

  dayNames: string[] = ["lun", "mar", "mié", "mue", "vie", "sáb", "dom"];

  statusConfig: { [key: string]: StatusConfig } = {
    'Pagado': { label: this.translate.instant('CALENDAR.PAID'), color: "bg-[#EBFCF2] text-success" },
    'Programado': { label: this.translate.instant('CALENDAR.PENDING'), color: "bg-[#e0e3ff] text-verified" },
    'Aplazado': { label: this.translate.instant('CALENDAR.DELAY'), color: "bg-gray-10 text-eggplant-100" },
    'Cancelado': { label: this.translate.instant('CALENDAR.CANCELED'), color: "bg-[#FDEDED] text-alert" },
    'Estimado': { label: this.translate.instant('PAYMENT_CALENDAR.ESTIMATED'), color: "bg-cream-100 text-eggplant-100" },
  };

  payments: Payment[] = [];

  currentDate: Date = new Date();
  selectedDate: Date | null = null;
  selectedDatePayments: Payment[] = [];

  calendarDaysGrid: DisplayDay[] = [];

  viewMode: 'month' | 'year' = 'year';
  yearlyCalendar: YearMonth[] = [];

  private initialDate: Date = new Date();

  yearOffset: number = 0;
  minPaymentDate: Date | null = null;
  maxPaymentDate: Date | null = null;

  constructor(private translate: TranslateService) {
    const today = new Date();
    this.currentDate.setFullYear(today.getFullYear(), today.getMonth(), today.getDate());
    this.currentDate.setDate(1); // Siempre empezar el mes en el día 1
    this.initialDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
  }

  ngOnInit() {
    for (let i = 0; i < this.calendarBenefits.length; i++) {

      if (this.calendarBenefits[i].benefits && this.calendarBenefits[i].benefits.length > 0) {
        const benef = this.calendarBenefits[i].benefits;
        for (let j = 0; j < benef.length; j++) {
          if (benef[j].date && benef[j].date.length === 3) {
            const objModel = {
              id: i + 1,
              date: `${benef[j].date[2]}/${benef[j].date[1]}/${benef[j].date[0]}`,
              title: benef[j].paymentTitle,
              project: benef[j].projectName,
              amount: benef[j].amount,
              status: benef[j].paymentStatus || 'Estimado',
              description: benef[j].paymentDescription,
              type: benef[j].type,
              sharePremiumOrRedemption: benef[j].share_premium_or_redemption,
              profitOrInterest: benef[j].profit_or_interest,
              showBreakdown: false,
            }
            this.payments.push(objModel)
          }
        }
      }
    }

    // Calcular rango de fechas de los pagos
    this.calculatePaymentDateRange();

    this.generateYearlyCalendar();
    const today = new Date();
    const todayForDemo = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    this.onDayClick(todayForDemo);
  }

  calculatePaymentDateRange(): void {
    if (!this.calendarBenefits || this.calendarBenefits.length === 0) {
      this.minPaymentDate = null;
      this.maxPaymentDate = null;
      return;
    }

    const allDates: Date[] = [];

    // Recorrer todos los benefits y extraer todas las fechas
    for (let i = 0; i < this.calendarBenefits.length; i++) {
      if (this.calendarBenefits[i].benefits && this.calendarBenefits[i].benefits.length > 0) {
        const benef = this.calendarBenefits[i].benefits;
        for (let j = 0; j < benef.length; j++) {
          if (benef[j].date && benef[j].date.length === 3) {
            // benef[j].date es [año, mes, día]
            const year = benef[j].date[0];
            const month = benef[j].date[1] - 1; // Los meses en JS van de 0-11
            const day = benef[j].date[2];
            allDates.push(new Date(year, month, day));
          }
        }
      }
    }

    if (allDates.length === 0) {
      this.minPaymentDate = null;
      this.maxPaymentDate = null;
      return;
    }

    // Encontrar min y max sin spread operator
    let minTime = allDates[0].getTime();
    let maxTime = allDates[0].getTime();

    for (let i = 1; i < allDates.length; i++) {
      const time = allDates[i].getTime();
      if (time < minTime) minTime = time;
      if (time > maxTime) maxTime = time;
    }

    this.minPaymentDate = new Date(minTime);
    this.maxPaymentDate = new Date(maxTime);

    // Ajustar al primer día del mes
    this.minPaymentDate.setDate(1);
    this.maxPaymentDate.setDate(1);
  }

  private parseDate(dateString: string): Date {
    const [day, month, year] = dateString.split("/").map(Number);
    const fullYear = year < 100 ? 2000 + year : year;
    return new Date(fullYear, month - 1, day);
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  }

  generateCalendarGrid(): void {
    this.calendarDaysGrid = [];
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    // Calcular el día de la semana para el primer día del mes (0=Domingo, 1=Lunes, etc.)
    let startDayOfWeek = firstDayOfMonth.getDay(); // 0 (Domingo) - 6 (Sábado)

    // Ajustar para que el Lunes sea 0 y el Domingo sea 6
    // Si es Domingo (0), queremos que sea 6 (último día en nuestra nueva semana)
    // Si es Lunes (1), queremos que sea 0 (primer día)
    // Formula: (startDayOfWeek + 6) % 7
    let adjustedStartDayOffset = (startDayOfWeek + 6) % 7;


    // Rellenar días del mes anterior
    for (let i = 0; i < adjustedStartDayOffset; i++) {
      const prevMonthDay = new Date(year, month, 1 - (adjustedStartDayOffset - i));
      this.calendarDaysGrid.push({
        date: prevMonthDay,
        dayOfMonth: prevMonthDay.getDate(),
        dayOfWeek: prevMonthDay.getDay(),
        payments: [],
        isCurrentMonth: false,
      });
    }

    // Rellenar días del mes actual
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      const cellDate = new Date(year, month, day);
      const dayPayments = this.payments.filter((payment) =>
        this.isSameDay(this.parseDate(payment.date), cellDate)
      );
      this.calendarDaysGrid.push({
        date: cellDate,
        dayOfMonth: day,
        dayOfWeek: cellDate.getDay(),
        payments: dayPayments,
        isCurrentMonth: true,
      });
    }

    // Rellenar días del mes siguiente
    const totalDaysInGrid = this.calendarDaysGrid.length;
    const remainingDays = 7 - (totalDaysInGrid % 7);
    if (remainingDays < 7 && remainingDays > 0) {
      for (let i = 1; i <= remainingDays; i++) {
        const nextMonthDay = new Date(year, month + 1, i);
        this.calendarDaysGrid.push({
          date: nextMonthDay,
          dayOfMonth: nextMonthDay.getDate(),
          dayOfWeek: nextMonthDay.getDay(),
          payments: [],
          isCurrentMonth: false,
        });
      }
    }
  }

  goToPreviousMonth(): void {
    if (this.canGoToPreviousMonth()) {
      this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
      this.generateCalendarGrid();
    }
  }

  // Modifica goToNextMonth
  goToNextMonth(): void {
    if (this.canGoToNextMonth()) {
      this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
      this.generateCalendarGrid();
    }
  }

  canGoToPreviousMonth(): boolean {
    if (!this.minPaymentDate) return false;

    const previousMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
    return previousMonth >= this.minPaymentDate;
  }

  canGoToNextMonth(): boolean {
    if (!this.maxPaymentDate) return false;

    const nextMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
    return nextMonth <= this.maxPaymentDate;
  }

  isPreviousMonthDisabled(): boolean {
    return !this.canGoToPreviousMonth();
  }

  // Modifica isNextMonthDisabled
  isNextMonthDisabled(): boolean {
    return !this.canGoToNextMonth();
  }

  goToToday(): void {
    this.currentDate = new Date(this.initialDate.getFullYear(), this.initialDate.getMonth(), 1);
    this.generateCalendarGrid();
    const today = new Date();
    const todayForDemo = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    this.onDayClick(todayForDemo);
    this.scrollToDetailContainer();
  }

  onDayClick(dayDate?: Date): void {
    this.selectedDate = dayDate;
    this.selectedDatePayments = this.payments.filter((payment) =>
      this.isSameDay(this.parseDate(payment.date), dayDate)
    );
    if (this.selectedDatePayments.length > 0) {
      this.scrollToDetailContainer();
    }
  }

  scrollToDetailContainer(): void {
    this.detailContainer.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }

  getPaymentStatusClass(status: string): string {
    const config: { [key: string]: StatusConfig } = this.statusConfig;
    return config[status] ? config[status].color : '';
  }

  getMonthYearTitle(): string {
    return `${this.monthNames[this.currentDate.getMonth()]} ${this.currentDate.getFullYear()}`;
  }

  formatSelectedDayDate(): string {
    if (!this.selectedDate) {
      return '';
    }
    const options: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    let formattedDate = this.selectedDate.toLocaleDateString('es-ES', options);

    const parts = formattedDate.split(' ');
    parts[0] = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    for (let i = 0; i < parts.length; i++) {
      if (['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'].includes(parts[i].toLowerCase())) {
        parts[i] = parts[i].charAt(0).toUpperCase() + parts[i].slice(1);
      }
    }
    return parts.join(' ');
  }

  isTodayDay(date: Date): boolean {
    const todayForDemo = new Date(2025, 5, 17);
    return this.isSameDay(date, todayForDemo);
  }

  isSelectedDayInGrid(date: Date): boolean {
    return !!this.selectedDate && this.isSameDay(date, this.selectedDate);
  }

  switchView(view: 'month' | 'year') {
    this.viewMode = view;
    if (view === 'year') {
      this.generateYearlyCalendar();
    } else {
      this.generateCalendarGrid();
    }
  }

  /** Construye los 12 meses siguientes empezando por currentDate */
  generateYearlyCalendar(): void {
    this.yearlyCalendar = [];

    const today = new Date();
    const baseYear = today.getFullYear();
    const baseMonth = today.getMonth();

    // Cada yearOffset representa un bloque de 12 meses (paginación)
    const monthsToAdd = this.yearOffset * 12;

    // Calcular el mes inicial y normalizar el año
    let startMonthTotal = baseMonth + monthsToAdd;
    let startYear = baseYear + Math.floor(startMonthTotal / 12);
    startMonthTotal = ((startMonthTotal % 12) + 12) % 12; // normaliza a 0–11

    // Generar los 12 meses a partir del mes inicial
    for (let i = 0; i < 12; i++) {
      const monthIndex = (startMonthTotal + i) % 12;
      const yearOffsetLocal = Math.floor((startMonthTotal + i) / 12);
      const actualYear = startYear + yearOffsetLocal;

      const firstDay = new Date(actualYear, monthIndex, 1);
      const daysInMonth = new Date(actualYear, monthIndex + 1, 0).getDate();
      const startDayOfWeek = (firstDay.getDay() + 6) % 7;

      const days: any[] = [];

      for (let j = 0; j < startDayOfWeek; j++) {
        days.push(null);
      }

      for (let d = 1; d <= daysInMonth; d++) {
        const dateObj = new Date(actualYear, monthIndex, d);

        const payments = this.payments.filter(p =>
          this.isSameDay(this.parseDate(p.date), dateObj)
        );

        const hasEvent = payments.length > 0;
        const highestStatus = hasEvent ? this.getHighestPriorityStatus(payments) : null;

        days.push({ day: d, hasEvent, status: highestStatus });
      }

      this.yearlyCalendar.push({
        month: monthIndex,
        year: actualYear,
        days,
      });
    }
  }

  private getHighestPriorityStatus(payments: Payment[]): string {
    const priority = ['Programado', 'Aplazado', 'Cancelado', 'Pagado', 'Estimado'];
    for (const status of priority) {
      if (payments.find(p => p.status === status)) {
        return status;
      }
    }
    return payments[0]?.status ?? '';
  }

  getStatusColorClass(status: string): string {
    switch (status) {
      case 'Programado':
        return 'bg-[#e0e3ff] text-verified rounded-full';
      case 'Aplazado':
        return 'bg-gray-10 text-eggplant-100 rounded-full';
      case 'Cancelado':
        return 'bg-orange-100 text-white rounded-full line-through decoration-eggplant-80 decoration-2';
      case 'Pagado':
        return 'bg-[#EBFCF2] text-success rounded-full';
      case 'Estimado':
        return 'bg-cream-100 text-eggplant-100 rounded-full';
      default:
        return '';
    }
  }

  onYearDayClick(year: number, month: number, day: number): void {
    const selectedDate = new Date(year, month, day);
    this.onDayClick(selectedDate);
  }

  goToPreviousYear(): void {
    if (this.canGoToPreviousYear()) {
      this.yearOffset--;
      this.generateYearlyCalendar();
    }
  }

  // Modifica goToNextYear
  goToNextYear(): void {
    if (this.canGoToNextYear()) {
      this.yearOffset++;
      this.generateYearlyCalendar();
    }
  }

  canGoToPreviousYear(): boolean {
    if (!this.minPaymentDate) return false;

    const today = new Date();
    const baseYear = today.getFullYear();
    const baseMonth = today.getMonth();

    // Rango anterior de 12 meses
    const monthsToAdd = (this.yearOffset - 1) * 12;

    // Primer mes del bloque anterior
    const startMonthTotal = baseMonth + monthsToAdd;
    const startYear = baseYear + Math.floor(startMonthTotal / 12);
    const normalizedStartMonth = ((startMonthTotal % 12) + 12) % 12;
    const firstMonthOfBlock = new Date(startYear, normalizedStartMonth, 1);

    // Último mes del bloque anterior (11 meses después)
    const lastMonthOfBlock = new Date(startYear, normalizedStartMonth + 11, 1);

    // Ajustar minPaymentDate al primer día del mes
    const firstPaymentMonth = new Date(this.minPaymentDate.getFullYear(), this.minPaymentDate.getMonth(), 1);

    // Permitimos retroceder si el bloque anterior contiene algún mes >= minPaymentDate
    return lastMonthOfBlock >= firstPaymentMonth;
  }


  canGoToNextYear(): boolean {
    if (!this.maxPaymentDate) return false;

    const today = new Date();
    const baseYear = today.getFullYear();
    const baseMonth = today.getMonth();

    // Rango siguiente de 12 meses
    const monthsToAdd = (this.yearOffset + 1) * 12;

    // OJO: el último mes visible es baseMonth + 11 (porque son 12 meses)
    const endMonthTotal = baseMonth + 11 + monthsToAdd;
    const endYear = baseYear + Math.floor(endMonthTotal / 12);
    const normalizedEndMonth = ((endMonthTotal % 12) + 12) % 12;

    // const lastDateOfRange = new Date(endYear, normalizedEndMonth, 1);

    // return lastDateOfRange <= this.maxPaymentDate;
    const lastDateOfRange = new Date(endYear, normalizedEndMonth + 1, 0);
    return lastDateOfRange <= this.maxPaymentDate ||
      (this.maxPaymentDate && lastDateOfRange.getFullYear() === this.maxPaymentDate.getFullYear());

  }

  getYearRangeTitle(): string {
    const today = new Date();
    const baseYear = today.getFullYear();
    const baseMonth = today.getMonth();
    const monthsToAdd = this.yearOffset * 12;

    const startMonthTotal = baseMonth + monthsToAdd;
    const startYear = baseYear + Math.floor(startMonthTotal / 12);
    const normalizedStartMonth = ((startMonthTotal % 12) + 12) % 12;

    const endMonthTotal = baseMonth + 11 + monthsToAdd;
    const endYear = baseYear + Math.floor(endMonthTotal / 12);
    const normalizedEndMonth = ((endMonthTotal % 12) + 12) % 12;

    return `${this.monthNames[normalizedStartMonth]} ${startYear} - ${this.monthNames[normalizedEndMonth]} ${endYear}`;
  }

}